"use client";

import { BookingDoctorGrid } from "@/components/BookingDoctorGrid";
import { useBookingSection } from "@/hooks/useBookingSection";
import { Doctor } from "@/types/doctor";
import { matchesEligibilityRules, type EligibilityPatient } from "@/utils/eligibility";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { needsBirthdate } from "@/components/BirthdateGate";
import { BIRTHDATE_SAVED } from "@/lib/authEvents";
const BookingModal = dynamic(() => import("@/components/BookingModal"), {
  ssr: false
});
const SignInModal = dynamic(() => import("@/components/SignInModal"), {
  ssr: false
});
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Modal from "@/components/Modal";
import { useTranslations } from "@/providers/TranslationsProvider";
dayjs.extend(utc);
dayjs.extend(timezone);

// First date this organizer can be booked on — their `first_availability`
// delay applied to today. Shared by the product list (so durations with no
// slots in range are never offered) and the availability fetch, which must
// agree or a duration survives the first and dead-ends on the second.
const firstBookableDate = (doctor: Doctor) => dayjs().add(doctor.first_availability || 0, 'day').format('YYYY-MM-DD');
export default function BookingSection({
  doctors: initialDoctors,
  translations,
  hasMore: hasMoreProp
}: {
  doctors: Doctor[];
  translations?: Record<string, string>;
  hasMore?: boolean;
}) {
  const {
    doctors,
    loading,
    hasMore,
    setSelectedDate,
    setSelectedTime,
    showModal,
    selectedDoctor,
    selectedDate,
    selectedTime,
    confirmed,
    setConfirmed,
    error,
    modalLoading,
    setModalLoading,
    products,
    selectedPriceUuid,
    setSelectedPriceUuid,
    availableDates,
    observerRef,
    setSelectedDoctor,
    setShowModal,
    fetchProducts,
    setAvailableDates
  } = useBookingSection(initialDoctors, hasMoreProp);
  const [confirming, setConfirming] = useState(false);
  // State for message modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalText, setMessageModalText] = useState("");
  const [messageModalType, setMessageModalType] = useState<"error" | "success" | "warning" | "info">("info");
  // State for sign in modal
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [pendingDoctor, setPendingDoctor] = useState<Doctor | null>(null);
  const [ineligible, setIneligible] = useState(false);
  const {
    t
  } = useTranslations();

  // Each DoctorCard tracks its own "loading" state locally, so clicking
  // card B while card A's openModal() is still awaiting checkAuth()/
  // fetchProducts() doesn't disable card B — both calls used to race to
  // set the same shared modal state, which looked like two modals
  // stacked/overlapping. Instead of blocking the second click, abort
  // whichever request is still in flight for A and switch straight to B.
  const activeRequestRef = useRef<AbortController | null>(null);
  // Set when the session turned out to be gone at pay time (e.g. the user
  // logged out in another tab): the booking modal is hidden behind the
  // sign-in modal with its selections kept, and reopened after sign-in.
  const [pendingPayment, setPendingPayment] = useState(false);
  // SignInModal calls onSuccess and then onClose after a verified code; this
  // tells onClose it's that close and not the user dismissing the modal.
  const signInSucceededRef = useRef(false);
  // Accounts need a birth date first (the header's BirthdateGate asks for it;
  // eligibility rules read it too). Booking waits here and resumes with this
  // doctor once it's saved.
  const [awaitingBirthdate, setAwaitingBirthdate] = useState<Doctor | null>(null);

  // Check authentication by calling /auth/me, refreshing the token once on 401
  const checkAuth = async (signal?: AbortSignal, retryCount = 0): Promise<Response> => {
    const maxRetries = 1;
    // full=1: the /users/:uuid record, which carries the birth date and gender
    // that the birth-date step and eligibility rules need.
    const response = await fetch("/api/v1/auth/me?full=1", {
      method: "GET",
      credentials: "include",
      // Include cookies
      cache: "no-store",
      signal
    });
    if (!response.ok && response.status === 401 && retryCount < maxRetries) {
      // Try refreshing token
      const refreshResponse = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
        signal
      });
      if (refreshResponse.ok) {
        // Retry checkAuth if refresh was successful
        return checkAuth(signal, retryCount + 1);
      }
    }
    return response;
  };

  // Modal handlers
  const openModal = async (doctor: Doctor) => {
    activeRequestRef.current?.abort();
    const controller = new AbortController();
    activeRequestRef.current = controller;
    const {
      signal
    } = controller;
    let patient: EligibilityPatient = {};
    try {
      const response = await checkAuth(signal);
      if (!response.ok) {
        setPendingDoctor(doctor);
        setShowSignInModal(true);
        return;
      }
      const data = await response.json();
      if (data?.user) patient = data.user;
      if (needsBirthdate(data?.user)) {
        setAwaitingBirthdate(doctor);
        return;
      }
    } catch (err) {
      // A newer click aborted this one — it's been superseded, not failed;
      // let the newer call's own state updates win instead of falling
      // through to the sign-in modal.
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      setPendingDoctor(doctor);
      setShowSignInModal(true);
      return;
    }
    setIneligible(!matchesEligibilityRules(doctor.eligibility, patient));
    setSelectedDoctor(doctor);
    setShowModal(true);
    setSelectedDate(availableDates[0]?.value || "");
    setSelectedTime("");
    setSelectedPriceUuid("");
    setConfirmed(false);
    // Optionally reset confirmation state
    if (doctor?.uuid) {
      const result = await fetchProducts(doctor.uuid, firstBookableDate(doctor), signal);
      if (signal.aborted) {
        return;
      }
      if (result.status === 401) {
        setShowModal(false);
        setPendingDoctor(doctor);
        setShowSignInModal(true);
      }
    }
  };
  useEffect(() => {
    if (!awaitingBirthdate) return;
    const resume = () => {
      const doctor = awaitingBirthdate;
      setAwaitingBirthdate(null);
      void openModal(doctor);
    };
    window.addEventListener(BIRTHDATE_SAVED, resume);
    return () => window.removeEventListener(BIRTHDATE_SAVED, resume);
    // openModal is recreated every render; the doctor is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingBirthdate]);
  const closeModal = () => {
    setShowModal(false);
    setSelectedDoctor(null);
    // Optionally reset confirmation state
  };
  const requireSignInForPayment = () => {
    setConfirming(false);
    setShowModal(false);
    setPendingPayment(true);
    setShowSignInModal(true);
  };
  const confirmBooking = async () => {
    if (!selectedDoctor || !selectedPriceUuid || !selectedDate || !selectedTime) {
      setMessageModalText(t("labels.please select all required fields"));
      setMessageModalType("warning");
      setShowMessageModal(true);
      return;
    }
    setConfirming(true);

    // The session may have ended since the modal opened (logout in another
    // tab, expired refresh token) — re-check it right before creating the
    // order instead of letting /pay fail or run without the user's token.
    let authResponse: Response | null = null;
    try {
      authResponse = await checkAuth();
    } catch {
      // Network failure — treated like a lost session below
    }
    if (!authResponse?.ok) {
      requireSignInForPayment();
      return;
    }
    try {
      // Slot wall-clock strings ("13:00") come from the availability API with
      // no zone attached — they're the DOCTOR's own local time (their
      // dashboard Regional Preferences), not the visitor's. Parsing them with
      // bare dayjs() would interpret them in the visitor's browser zone
      // instead, silently shifting the appointment by the offset between the
      // two zones. dayjs.tz(..., doctorTimezone) anchors the wall-clock string
      // to the doctor's zone before converting to the UTC instant we send.
      const doctorTimezone = selectedDoctor.timezone || "Asia/Tehran";
      const selectedDateTime = dayjs.tz(`${selectedDate} ${selectedTime}`, doctorTimezone).toISOString();
      // The slot already carries its own end clock-time from the availability
      // API — reuse it instead of guessing a duration, so the appointment
      // range shown downstream (dashboard/admin/profile/email) is exact.
      const slot = availableDates.find(day => day.value === selectedDate)?.times.find(t => t.start === selectedTime);
      const selectedEndDateTime = slot?.end ? dayjs.tz(`${selectedDate} ${slot.end}`, doctorTimezone).toISOString() : undefined;
      const res = await fetch("/api/v1/pay", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          doctorUuid: selectedDoctor.uuid,
          priceUuid: selectedPriceUuid,
          selectedDateTime: selectedDateTime,
          // Send as combined timezone-aware datetime
          selectedEndDateTime
        })
      });
      if (res.status === 401) {
        requireSignInForPayment();
        return;
      }
      if (!res.ok) {
        throw new Error("Payment failed");
      }
      window.location.href = (await res.json()).redirectUrl;
    } catch (err: Error | unknown) {
      setConfirming(false);
      setMessageModalText(t("labels.payment error") + (err instanceof Error ? err.message : String(err)));
      setMessageModalType("error");
      setShowMessageModal(true);
    }
  };
  const fetchAvailability = async (priceUuid?: string) => {
    const doctor = selectedDoctor;
    const prUuid = priceUuid || selectedPriceUuid;
    if (!doctor || !prUuid) {
      return;
    }
    try {
      setModalLoading(true);
      const fromDate = firstBookableDate(doctor);
      const res = await fetch(`/api/v1/users/${doctor.uuid}/availability?priceUuid=${prUuid}&from=${fromDate}`, {
        cache: 'no-store'
      });
      const json = await res.json();
      if (Array.isArray(json)) {
        // Set available dates for the modal
        setSelectedDate(json[0]?.value || "");
        setAvailableDates(json);
      } else {
        setAvailableDates([]);
      }
    } catch {
      setAvailableDates([]);
    } finally {
      setModalLoading(false);
    }
  };
  return <>
      <BookingDoctorGrid doctors={doctors} loading={loading} hasMore={hasMore} observerRef={observerRef} onBook={openModal} translations={{
      noMoreDoctors: t("labels.end of results", "End of results"),
      ...translations
    }} />
      <BookingModal show={showModal} doctor={selectedDoctor} selectedDate={selectedDate} selectedTime={selectedTime} confirmed={confirmed} error={error} loading={modalLoading} products={products} selectedPriceUuid={selectedPriceUuid} onClose={closeModal} onDateChange={setSelectedDate} onTimeChange={setSelectedTime} onConfirm={confirmBooking} confirming={confirming} fetchAvailability={fetchAvailability} onProductSelect={priceUuid => {
      setSelectedPriceUuid(priceUuid);
    }} onCancelConfirming={() => setConfirming(false)} availableDates={availableDates} ineligible={ineligible} />
      {/* Reusable message modal */}
      <Modal show={showMessageModal} onClose={() => setShowMessageModal(false)} type={messageModalType} variant="message" size="sm">
        <div className="py-2">{messageModalText}</div>
      </Modal>
      {/* Sign In Modal */}
      <SignInModal show={showSignInModal} disableUrlSync notice={pendingPayment ? t("auth.signed out notice", "You've been signed out. Please sign in again to continue your booking — your selected time is saved.") : undefined} onClose={() => {
      setShowSignInModal(false);
      const succeeded = signInSucceededRef.current;
      signInSucceededRef.current = false;
      if (pendingPayment && !succeeded) {
        setPendingPayment(false);
        closeModal();
      }
    }} onSuccess={async () => {
      signInSucceededRef.current = true;
      if (pendingPayment && selectedDoctor) {
        // Reopen the booking with the chosen slot intact; eligibility is
        // re-checked since a different account may have signed in.
        let patient: EligibilityPatient = {};
        try {
          const response = await fetch("/api/v1/auth/me?full=1", {
            method: "GET",
            credentials: "include"
          });
          const data = await response.json();
          if (data?.user) patient = data.user;
        } catch {
          // Empty patient — see the fallback note below
        }
        setIneligible(!matchesEligibilityRules(selectedDoctor.eligibility, patient));
        setPendingPayment(false);
        setShowModal(true);
      } else if (pendingDoctor) {
        // Same path as a signed-in click: checks the birth date and
        // eligibility for the account that just signed in.
        await openModal(pendingDoctor);
        setPendingDoctor(null);
      }
      setShowSignInModal(false);
    }} />
      {/* Modal animation */}
      <style>{`
        .animate-slide-up { animation: slideUp 0.3s cubic-bezier(.4,2,.6,1) both; }
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </>;
}